import {
  Box,
  Button,
  Divider,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { CONTENT_EDITOR } from 'routes/constants'
import RichTextContentEditor from './RichTextEditor'
import { patchRequest } from 'utils/api'
import { GOAL_STEP_QUIZ_URL, JOURNEY_STEPS_SECTIONS } from 'services/constants'
import RequestRetryAlert from 'components/atoms/RequestRetryAlert'
import RowStack from 'components/atoms/container/RowStack'
import InCardStack from 'components/atoms/container/InCardStack'
import SourceEditor from './SourceEditor'
import SourceOutlinedIcon from '@mui/icons-material/SourceOutlined'
import {
  AccordionHead,
  StyledAccordionJourney
} from 'components/atoms/container/Accordion'
import {
  logScreenAction,
  trackingSubScreenKey
} from 'services/trackingScreenActions'
import { useTranslation } from 'react-i18next'

const TextInput = ({ initialValue, onChange, disabled }) => {
  const { t } = useTranslation()
  const [value, setValue] = useState('')
  useEffect(() => {
    setValue(initialValue || '')
  }, [initialValue])

  const handleChange = event => {
    setValue(event.target.value)
    onChange(event)
  }
  return (
    <TextField
      value={value}
      onChange={handleChange}
      disabled={disabled}
      multiline
      rows={3}
      placeholder={t('stepQuiz.yourResponse')}
      sx={{
        '& .MuiInputBase-root': {
          padding: 8
        }
      }}
    />
  )
}

const EditorModule = ({
  initialValue,
  value,
  setValue,
  saved,
  setSaved,
  title,
  handleSave,
  handleNull
}) =>
  value ? (
    <>
      <Divider sx={{ width: '100%' }} />
      <RowStack width='unset'>
        <Typography variant='h4'>{title} Editor</Typography>
        <Button onClick={() => handleSave()} disabled={saved}>
          Save
        </Button>
        <Button
          onClick={() => {
            setValue(null)
            handleNull()
          }}>
          Clear
        </Button>
      </RowStack>
      <RichTextContentEditor
        data={initialValue}
        changeCallback={updated => {
          setValue(updated)
          setSaved(false)
        }}
      />
      <Typography variant='h5'>Preview</Typography>
      <Divider sx={{ width: '100%' }} />
      <Box
        width='100%'
        dangerouslySetInnerHTML={{ __html: value }}
        className='journeyStepContentInnerHTML'
      />
      <Typography variant='h5'>End of {title} Editor</Typography>
      <Divider sx={{ width: '100%' }} />
    </>
  ) : (
    <Button onClick={() => setValue(' ')}>Add {title}</Button>
  )

const StepQuizText = ({ stepData, submitted, handleAnswerChange, answers }) => {
  const location = useLocation()
  const { t } = useTranslation()
  const editorMode = location.pathname.includes(CONTENT_EDITOR)

  const [summary, setSummary] = useState(undefined)
  const [summaryEnd, setSummaryEnd] = useState(undefined)
  const [saved, setSaved] = useState(true)
  const [requestError, setRequestError] = useState(undefined)
  const [sources, setSources] = useState(undefined)

  useEffect(() => {
    setSummary(stepData.summary)
    setSummaryEnd(stepData.summaryEnd)
    setSources(stepData.sources)
  }, [stepData])

  const handleSave = async (s, se, so = sources) => {
    const requestBody = {
      summary: s,
      summaryEnd: se,
      sources: so
    }
    const res = await patchRequest(
      `${GOAL_STEP_QUIZ_URL}/${stepData.id}`,
      requestBody
    )
    if (res.status === 200) {
      setSaved(true)
      setRequestError(false)
      stepData.summary = s
      stepData.summaryEnd = se
      stepData.sources = so
    } else {
      setRequestError(true)
    }
  }

  const handleSourceClick = e => {
    const aTag = e.target.closest('a')
    if (aTag) {
      const linkClicked = aTag.getAttribute('href')
      if (linkClicked) {
        logScreenAction('source_click', linkClicked, trackingSubScreenKey)
      }
    }
  }

  return (
    <InCardStack spacing={12} paddingBottom={8}>
      {requestError ? <RequestRetryAlert /> : null}
      {editorMode ? (
        <EditorModule
          initialValue={stepData.summary}
          value={summary}
          setValue={setSummary}
          saved={saved}
          setSaved={setSaved}
          title='Summary'
          handleSave={() => handleSave(summary, summaryEnd)}
          handleNull={() => handleSave(null, summaryEnd)}
        />
      ) : (
        <Box
          width='100%'
          dangerouslySetInnerHTML={{ __html: summary }}
          className='journeyStepContentInnerHTML'
        />
      )}
      {stepData.questions.map((q, idx) => (
        <Stack key={`question-${idx}`} spacing={10} padding={0} width='100%'>
          <Box
            width='100%'
            dangerouslySetInnerHTML={{ __html: q.question }}
            className='journeyStepContentInnerHTML'
          />
          <TextInput
            initialValue={answers[idx]}
            onChange={handleAnswerChange(idx)}
            disabled={submitted}
          />
        </Stack>
      ))}
      {editorMode ? (
        <EditorModule
          initialValue={stepData.summaryEnd}
          value={summaryEnd}
          setValue={setSummaryEnd}
          saved={saved}
          setSaved={setSaved}
          title='End Summary'
          handleSave={() => handleSave(summary, summaryEnd)}
          handleNull={() => handleSave(summary, null)}
        />
      ) : (
        <Box
          width='100%'
          dangerouslySetInnerHTML={{ __html: summaryEnd }}
          className='journeyStepContentInnerHTML'
        />
      )}

      {editorMode ? (
        <SourceEditor
          sources={sources}
          setSources={setSources}
          initialSources={stepData.sources}
          saved={saved}
          handleSave={() => handleSave(summary, summaryEnd)}
          sourcesEditorCallback={updated => {
            setSources(updated)
            setSaved(false)
          }}
          handleClearSource={() => {
            setSources(null)
            handleSave(summary, summaryEnd, null)
          }}
        />
      ) : stepData.sources ? (
        <StyledAccordionJourney
          id={JOURNEY_STEPS_SECTIONS.SOURCES}
          trackingEvent='sources'
          summaryComponent={
            <AccordionHead
              icon={<SourceOutlinedIcon />}
              heading={t('stepNote.sources')}
              headingProps={{ 'data-testid': 'sourcesAccordion' }}
            />
          }>
          <Box
            onClick={handleSourceClick}
            dangerouslySetInnerHTML={{ __html: stepData.sources }}
            className='journeyStepContentInnerHTML'
          />
        </StyledAccordionJourney>
      ) : null}
    </InCardStack>
  )
}

export default StepQuizText
